import React, { useState } from 'react';
import { Skill } from '../../../types/profile';
import { EditableSection } from '../common/EditableSection';

interface SkillsSectionProps {
  skills: Skill[];
  onUpdate: (skills: Skill[]) => void;
}

export const SkillsSection: React.FC<SkillsSectionProps> = ({
  skills,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editSkills, setEditSkills] = useState<string[]>(skills.map(s => s.skill));
  const [newSkill, setNewSkill] = useState('');

  const handleEdit = () => {
    setEditSkills(skills.map(s => s.skill));
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditSkills(skills.map(s => s.skill));
    setNewSkill('');
    setIsEditing(false);
  };

  const handleSave = () => {
    onUpdate(editSkills.map(skill => ({ skill })));
    setNewSkill('');
    setIsEditing(false);
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !editSkills.includes(newSkill.trim())) {
      setEditSkills([...editSkills, newSkill.trim()]);
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (index: number) => {
    setEditSkills(editSkills.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSkill();
    }
  };

  return (
    <EditableSection
      title="Skills"
      isEditing={isEditing}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
    >
      {isEditing ? (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {editSkills.map((skill, index) => (
              <div
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800"
              >
                <span>{skill}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveSkill(index)}
                  className="ml-2 inline-flex items-center justify-center w-4 h-4 text-indigo-600 hover:text-indigo-800"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          <div className="flex space-x-2">
            <input
              type="text"
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Add a skill"
              maxLength={50}
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
            <button
              type="button"
              onClick={handleAddSkill}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Add
            </button>
          </div>
        </div>
      ) : (
        <div>
          {skills.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {skills.map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                >
                  {skill.skill}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">No skills added yet.</p>
          )}
        </div>
      )}
    </EditableSection>
  );
};